import React, {useCallback, useEffect, useMemo, useState} from "react";
import {numberValidator} from "../../../../utils/Validator";
import {Button, Input, message, Select, Space, Table} from "antd";

import {ColumnsType} from "antd/es/table";
import {PackingData} from "../../../../types/PackingData";
import {
    addPackingRow,
    deletePackingByPackId,
    fetchPackingByPid,
    updatePackingRow
} from "../../../../backendInteraction/PackingApiFetch";

interface PackingTableProps {
    data: PackingData[];
    plodId: number;
    contractNo: string;
    isView: boolean;
    isAddNewRow: boolean | null;
    setIsAddNewRow: React.Dispatch<React.SetStateAction<boolean>> | null;
    holeId: string;
    setHoleId: (value: string) => void;
}

const PackingTable: React.FC<PackingTableProps> = ({ data, plodId, contractNo, isView, isAddNewRow, setIsAddNewRow, holeId, setHoleId }) => {
    const [packingData, setPackingData] = useState<PackingData[]>([]);
    const [editingPackId, setEditingPackId] = useState<number | null>(null);
    const [editData, setEditData] = useState<PackingData | null>(null);

    const pluggedOption: { label: string; value: string }[] = [
        { label: 'Yes', value: 'Yes' },
        { label: 'No', value: 'No' },
    ];

    useEffect(() => {
        setPackingData(data);
    }, [data]);

    useEffect(() => {
        if (isAddNewRow === true && editingPackId !== -1) {
            const len = packingData.length;
            const newRow: PackingData = {
                key: -1,
                pack_id: -1,
                hole_id: len > 0 ? packingData[len - 1].hole_id || holeId : holeId,
                depth: "",
                plugged: "",
                comment: "",
                pid: plodId ?? -1,
            };
            setPackingData(prevData => {
                const hasNewRow = prevData.some(item => item.pack_id === -1);
                return hasNewRow ? prevData : [...prevData, newRow];
            });

            setEditingPackId(-1);
            setEditData(newRow);
        }
    }, [isAddNewRow, editingPackId]);

    useEffect(() => {
        if (isView) {
            if (editingPackId === -1) {
                setPackingData(prevData => prevData.filter(item => item.pack_id !== -1));
                setIsAddNewRow && setIsAddNewRow(false);
            }

            setEditingPackId(null);
            setEditData(null);
        }
    }, [isView]);

    const saveChanges = useCallback(async () => {
        console.log("Saving changes:", editData);

        if (!editData) return;

        const { depth, hole_id } = editData;


        if(!!depth) {
            const isNumber = numberValidator(depth, 'Depth');
            if (!isNumber) return;
            const q = parseFloat(depth);
            if (q < 0) {
                message.error('The "Depth" value cannot be less than 0.');
                return;
            }
        }

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        if (editingPackId === -1) {
            const success = await addPackingRow(editData, aid);
            if (success) {
                setPackingData(await fetchPackingByPid(plodId ?? -1, aid));
                message.success("New packing added successfully!");
            } else {
                message.error("Failed to add new packing.");
            }
        } else {
            const success = await updatePackingRow(editingPackId ?? -1, editData);
            if (success) {
                setPackingData(await fetchPackingByPid(plodId ?? -1, aid));
                message.success("Packing updated successfully!");
            } else {
                message.error("Failed to update packing.");
            }
        }

        !(hole_id === holeId) && setHoleId(hole_id);

        if (editingPackId === -1 && setIsAddNewRow) {
            setIsAddNewRow(false);
        }

        setEditingPackId(null);
        setEditData(null);
    }, [editData, editingPackId, setIsAddNewRow]);

    const cancelEditing = useCallback((record: PackingData) => {
        console.log("Cancel editing");
        setEditingPackId(null);
        setEditData(null);

        if (record.pack_id === -1) {
            setPackingData(prevData => prevData.filter(item => item.pack_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }
    }, [setIsAddNewRow]);

    const startEditing = useCallback((record: PackingData) => {
        console.log("Editing row:", record);

        if (editingPackId === -1) {
            setPackingData(prevData => prevData.filter(item => item.pack_id !== -1));
            setIsAddNewRow && setIsAddNewRow(false);
        }

        setEditingPackId(prevId => {
            if (prevId !== null) cancelEditing({ pack_id: prevId } as PackingData);
            return record.pack_id;
        });

        setEditData({ ...record });
    }, [editingPackId, setIsAddNewRow]);

    const deleteRow = useCallback(async (record: PackingData) => {
        console.log("Deleting row:", record);

        const storedUser = localStorage.getItem("DORS_USER");
        const aid = storedUser && JSON.parse(storedUser).Aid;

        const success = await deletePackingByPackId(record.pack_id);
        if (success) {
            setPackingData(await fetchPackingByPid(plodId ?? -1, aid));
            message.success("Packing deleted successfully!");
        } else {
            message.error("Failed to delete packing.");
        }
    }, []);

    const handleInputChange = useCallback((key: keyof PackingData, value: string) => {
        if (editData) {
            setEditData(prevData => ({
                ...prevData!,
                [key]: value || "",
            }));
        }
    }, [editData]);

    const columns: ColumnsType<PackingData> = useMemo(() => [
        {
            title: "Hole ID",
            dataIndex: "hole_id",
            key: "hole_id",
            width: "12%",
            render: (_, record) =>
                editingPackId === record.pack_id ? (
                    <Input
                        value={editData?.hole_id}
                        onChange={(e) => handleInputChange("hole_id", e.target.value)}
                    />
                ) : (
                    record.hole_id
                ),
        },
        {
            title: "Depth",
            dataIndex: "depth",
            key: "depth",
            width: "12%",
            render: (_, record) =>
                editingPackId === record.pack_id ? (
                    <Input
                        value={editData?.depth}
                        onChange={(e) => handleInputChange("depth", e.target.value)}
                    />
                ) : (
                    record.depth
                ),
        },
        {
            title: "Plugged",
            dataIndex: "plugged",
            key: "plugged",
            width: "12%",
            minWidth: 280,
            render: (_, record) =>
                editingPackId === record.pack_id ? (
                    <Select
                        showSearch
                        allowClear={true}
                        style={{ width: "100%" }}
                        options={pluggedOption}
                        placeholder=""
                        filterOption={(input, option) =>
                            option?.label?.toLowerCase().includes(input.toLowerCase()) ?? false
                        }
                        value={editData?.plugged || ""}
                        onChange={(value) => handleInputChange("plugged", value || "")}
                    />
                ) : (
                    record.plugged
                ),
        },
        {
            title: "Comment",
            dataIndex: "comment",
            key: "comment",
            width: "40%",
            render: (_, record) =>
                editingPackId === record.pack_id ? (
                    <Input
                        value={editData?.comment}
                        onChange={(e) => handleInputChange("comment", e.target.value)}
                    />
                ) : (
                    record.comment
                ),
        },
        ...(!isView
            ? [
                {
                    title: "Action",
                    key: "action",
                    width: "24%",
                    render: (_, record) => (
                        <Space size="small">
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingPackId === record.pack_id ? saveChanges() : startEditing(record))}>
                                {editingPackId === record.pack_id ? "Save" : "Edit"}
                            </Button>
                            <Button color="geekblue" variant="text"
                                    onClick={() => (editingPackId === record.pack_id ? cancelEditing(record) : deleteRow(record))}>
                                {editingPackId === record.pack_id ? "Cancel" : "Delete"}
                            </Button>
                        </Space>
                    ),
                },
            ] as ColumnsType<PackingData>
            : []),
    ], [isView, editingPackId, editData, handleInputChange, saveChanges, startEditing, cancelEditing, deleteRow]);

    return (
        <Table<PackingData>
            columns={columns}
            pagination={false}
            dataSource={packingData}
        />
    );
};

export default PackingTable;